import React from 'react';

const SupportApp = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg p-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">AzucarAI Support</h1>
          <p className="text-lg text-gray-600">
            We're here to help you with any questions or issues
          </p>
        </div>
        
        <div className="space-y-8 text-gray-700">
          {/* Contact Information */}
          <section className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-lg">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="mr-3">📧</span>
              Contact Us
            </h2>
            <p className="mb-4 text-lg">
              For any support inquiries, technical issues, or questions about the AzucarAI mobile app, 
              please reach out to our support team:
            </p>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-xl font-semibold text-orange-600">
                <a 
                  href="mailto:info@azucarmultimedia.com" 
                  className="hover:text-orange-700 transition-colors underline"
                >
                  info@azucarmultimedia.com
                </a>
              </p>
            </div>
          </section>

          {/* Common Issues */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="mr-3">❓</span>
              Common Questions
            </h2>
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-lg text-gray-900 mb-2">
                  How do I enable GPS permissions?
                </h3>
                <p className="text-gray-700">
                  Go to your device Settings → AzucarAI → Location and select "While Using the App"
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-lg text-gray-900 mb-2">
                  How do I upload photos?
                </h3>
                <p className="text-gray-700">
                  From the home screen, tap "Upload Photo" and select either "Take Photo" to use your camera 
                  or "Gallery" to choose an existing photo. Add any relevant details and tap "Upload Image".
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-lg text-gray-900 mb-2">
                  Can I use the app without GPS?
                </h3>
                <p className="text-gray-700">
                  Yes! While GPS helps create accurate records of service locations, you can still upload 
                  photos without enabling location services.
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-lg text-gray-900 mb-2">
                  How do I switch between businesses?
                </h3>
                <p className="text-gray-700">
                  If you have admin permissions, you'll see a business selector on the home screen. 
                  Simply tap the dropdown and select the business you want to work with.
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-lg text-gray-900 mb-2">
                  Where can I view my uploaded photos?
                </h3>
                <p className="text-gray-700">
                  Tap "View Gallery" from the home screen to see all uploaded photos. You can also 
                  access them from your azucarai.com account on any device.
                </p>
              </div>
            </div>
          </section>

          {/* System Requirements */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="mr-3">📱</span>
              System Requirements
            </h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>iOS 13.0 or later</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Active azucarai.com account</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Internet connection for uploading photos</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Camera and/or photo library access (optional GPS for geotagging)</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Additional Help */}
          <section className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="mr-3">💡</span>
              Need More Help?
            </h2>
            <p className="mb-4">
              When contacting support, please include:
            </p>
            <ul className="space-y-2 text-gray-700 ml-4">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Your registered email address</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>A detailed description of the issue</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Device model and iOS version</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Screenshots (if applicable)</span>
              </li>
            </ul>
            <p className="mt-4 text-sm text-gray-600">
              Our support team typically responds within 24-48 business hours.
            </p>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-6 border-t border-gray-200 text-center">
          <p className="text-gray-600 mb-2">
            Powered by{" "}
            <a 
              href="https://azucarmultimedia.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-orange-600 hover:text-orange-700 font-semibold"
            >
              Azúcar Multimedia
            </a>
          </p>
          <p className="text-sm text-gray-500">
            © {new Date().getFullYear()} Azúcar Multimedia. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SupportApp;

